import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useOrganization } from '@/contexts/organization-context'
import { billingService, type UsageResponse } from '@/services/billing'
import { Button } from '@/components/ui/button'

interface Props {
  message?: string
}

/**
 * Full-page state for an organization the api's billing gate is blocking.
 *
 * Rendered INSTEAD of the app — mounting the normal tree fires a gated request per
 * component, all 403, producing a broken page. This page acts DIRECTLY (no popup):
 *   - free org (no subscription) → Stripe Checkout to add a card
 *   - paid org → approve the upgrade in place (card on file charged the difference)
 *   - paid org with NO card (legacy/failed onboarding) → the api returns 402
 *     no_payment_method, and we open the Customer Portal so they add a card first.
 */
export function BillingBlockedScreen({ message }: Props) {
  const { currentOrganization } = useOrganization()
  const [usage, setUsage] = useState<UsageResponse | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // /api/billing/usage is not behind the gate, so this still resolves.
    billingService.getUsage().then(setUsage).catch(() => {})
  }, [currentOrganization?.slug])

  const isFree = usage?.is_free !== false
  const cta = isFree ? 'Add payment method' : 'Approve upgrade'
  const used = (usage?.mtd_records ?? 0) + (usage?.mtd_backfill_records ?? 0)

  // Monthly price difference — both figures come straight from the band table via
  // /usage (subscription_plans.features), so no Stripe call is needed to show it.
  const monthlyIncrease =
    usage?.required_tier_price != null && usage?.committed_tier_price != null
      ? Math.max(usage.required_tier_price - usage.committed_tier_price, 0)
      : null

  const slug = currentOrganization?.slug

  const goToCheckout = async () => {
    const { url } = await billingService.createCheckoutSession({
      success_path: `/${slug}/settings?upgraded=1`,
      cancel_path: `/${slug}/upgrade`,
    })
    if (!url) throw new Error('No checkout URL returned')
    window.location.href = url
  }

  const goToPortalToAddCard = async () => {
    // Existing customer, no card on file → let Stripe collect one, then come back.
    const { url } = await billingService.createPortalSession(`/${slug}/upgrade`)
    if (!url) throw new Error('No portal URL returned')
    window.location.href = url
  }

  const onClick = async () => {
    if (loading || !slug) return
    setLoading(true)
    try {
      if (isFree) {
        await goToCheckout()
        return
      }
      const result = await billingService.approveUpgrade(usage?.required_tier ?? undefined)
      toast.success(`Upgraded to ${result.tier} — $${result.monthly_price.toLocaleString()}/mo`)
      // Reload so the gate re-evaluates and the app renders.
      window.location.href = `/${slug}`
    } catch (error: any) {
      const status = error?.response?.status
      const detail: string = error?.response?.data?.detail || error?.message || ''
      // Paid org whose card is missing/expired — send them to add one, then retry.
      if (status === 402 || detail.includes('no_payment_method')) {
        toast.message('Add a payment method to complete the upgrade.')
        try {
          await goToPortalToAddCard()
          return
        } catch {
          /* fall through to the generic error below */
        }
      }
      console.error('Upgrade failed:', error)
      toast.error(detail || 'Could not complete the upgrade. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-[70vh] p-6">
      <div className="max-w-md w-full rounded-xl border border-muted p-6 text-center">
        <h2 className="text-lg font-semibold">Upgrade required</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {message ||
            "Your organization has reached its plan's record limit. Upgrade to continue."}
        </p>

        {usage && (
          <div className="mt-4 rounded-lg bg-muted/40 p-3 text-sm text-left">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Records this month</span>
              <span className="font-medium">{used.toLocaleString()}</span>
            </div>
            {usage.committed_tier && (
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">Current plan</span>
                <span className="font-medium">
                  {usage.committed_tier}
                  {usage.committed_tier_price != null &&
                    ` — $${usage.committed_tier_price.toLocaleString()}/mo`}
                </span>
              </div>
            )}
            {usage.required_tier && usage.required_tier !== usage.committed_tier && (
              <>
                <div className="flex justify-between mt-1">
                  <span className="text-muted-foreground">New plan</span>
                  <span className="font-medium">
                    {usage.required_tier}
                    {usage.required_tier_price != null &&
                      ` — $${usage.required_tier_price.toLocaleString()}/mo`}
                  </span>
                </div>
                {monthlyIncrease != null && (
                  <div className="flex justify-between mt-2 border-t border-muted pt-2">
                    <span className="font-medium">Monthly increase</span>
                    <span className="font-semibold">
                      +${monthlyIncrease.toLocaleString()}/mo
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!isFree && monthlyIncrease != null && (
          <p className="mt-3 text-xs text-muted-foreground">
            Your card on file is charged the prorated difference immediately.
          </p>
        )}

        <Button className="mt-5 w-full" onClick={onClick} disabled={loading}>
          {loading ? 'Working…' : cta}
        </Button>
        <p className="mt-3 text-xs text-muted-foreground">
          Need help? Contact support and we'll sort it out.
        </p>
      </div>
    </div>
  )
}
